import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQuery } from 'react-query';
// import { Auth } from 'aws-amplify';

// setup AWS before use
// @TODO uncomment if user sections is back
// import './setup';

/* eslint-disable @typescript-eslint/no-explicit-any */
const Auth = {
  currentSession: (): Promise<any> => new Promise(() => { throw new Error('Not implemented'); }),
  currentAuthenticatedUser:
    (): Promise<any> => new Promise(() => { throw new Error('Not implemented'); }),
  signIn:
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (user: string, password: string): Promise<any> => (
      new Promise(() => { throw new Error('Not implemented'); })
    ),
  completeNewPassword:
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (user: AWSUserResponse | undefined, password: string): Promise<any> => (
      new Promise(() => { throw new Error('Not implemented'); })
    ),
  signOut: (): Promise<any> => new Promise(() => { throw new Error('Not implemented'); }),
};
/* eslint-enable @typescript-eslint/no-explicit-any */

export interface UserContextProps {
  isLoading: boolean;
  isLoggedIn: boolean;
  logout: () => void;
  signIn: (username: string, password: string) => Promise<boolean>;
  completePassword: (password: string) => Promise<void>;
  user?: AWSUserResponse;
  error?: Error;
}

export const UserContext = createContext<UserContextProps>({
  isLoading: false,
  isLoggedIn: false,
  logout: () => undefined,
  signIn: () => Promise.resolve(false),
  completePassword: () => Promise.resolve(undefined),
});

function Provider({ children }: { children?: ReactNode }) {
  const [user, setUser] = useState<AWSUserResponse>();

  const userRequest = useQuery<AWSUserResponse | undefined, Error>(
    'user',
    () => Auth.currentSession()
      .then((): Promise<AWSUserResponse> => Auth.currentAuthenticatedUser())
      .catch(() => undefined),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  );

  useEffect(() => {
    if (userRequest.isFetched) {
      setUser(userRequest.data);
    }
  }, [userRequest.data, userRequest.isFetched]);

  const signIn = useCallback(
    (username: string, password: string): Promise<boolean> => Auth.signIn(username, password)
      .then((response: AWSUserResponse) => {
        setUser(response);

        return response.challengeName === 'NEW_PASSWORD_REQUIRED';
      }),
    [],
  );

  const completePassword = useCallback(
    (password: string): Promise<void> => Auth.completeNewPassword(user, password)
      .then((newUser: AWSUserResponse) => {
        setUser(newUser);
      }),
    [user],
  );

  const logout = useCallback(() => {
    setUser(undefined);
    Auth.signOut();
  }, []);

  const value = useMemo(() => ({
    isLoading: userRequest.isLoading,
    error: userRequest.error || undefined,
    isLoggedIn: false,
    signIn,
    completePassword,
    user,
    logout,
  }), [completePassword, logout, signIn, user, userRequest.error, userRequest.isLoading]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export default Provider;
